import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { Redirect, Route, Switch } from 'react-router-dom'
import * as device from './device'
import { languages as allLanguages } from 'js/locale'
import SentryBoundary from './SentryBoundary'
import RouteChangeHandler, {
  withPageViewCategory,
} from './components/RouteChangeHandler'
import LanguageProvider from './components/LanguageProvider'
import PriceUpdater from './components/PriceUpdater'
import { hot } from 'react-hot-loader/root'
import { MobileMenuProvider } from './hooks/useMobileMenu'
import { AbTestProvider } from './hooks/useAbTest'
import { Title } from 'react-head'
import { ga } from 'js/analytics/adapters'

import loadable from '@loadable/component'
import { isSSR } from 'js/isSSR'
import useTracking from 'js/hooks/useTracking'

const RedirectToOldWeb = loadable(() =>
  import(
    /* webpackChunkName: "RedirectToOldWeb" */ './components/RedirectToOldWeb'
  )
)
const RedirectToOtherSites = loadable(() =>
  import(
    /* webpackChunkName: "RedirectToOtherSites" */ './components/RedirectToOtherSites'
  )
)
const ActivateGiftCardPage = loadable(() =>
  import(
    /* webpackChunkName: "ActivateGiftCardPage" */ './pages/ActivateGiftCardPage/ActivateGiftCardPage'
  )
)
const BuyGiftCardPage = loadable(() =>
  import(
    /* webpackChunkName: "BuyGiftCardPage" */ './pages/BuyGiftCardPage/BuyGiftCardPage'
  )
)
const BuyGiftCardDonePage = loadable(() =>
  import(
    /* webpackChunkName: "BuyGiftCardDonePage" */ './pages/BuyGiftCardPage/BuyGiftCardDonePage'
  )
)

const EmailLandingPage = loadable(() =>
  import(/* webpackChunkName: "EmailLandingPage" */ './pages/EmailLandingPage')
)
const FeaturesPage = loadable(() =>
  import(
    /* webpackChunkName: "FeaturesPage" */ './pages/FeaturesPage/FeaturesPage'
  )
)
const FinalizePaymentPage = loadable(() =>
  import(
    /* webpackChunkName: "FinalizePaymentPage" */ './pages/FinalizePaymentPage/FinalizePaymentPage'
  )
)
const FriskvardPage = loadable(() =>
  import(
    /* webpackChunkName: "FriskvardPage" */ './pages/FriskvardPage/FriskvardPage'
  )
)
const GoogleAssistantPage = loadable(() =>
  import(
    /* webpackChunkName: "GoogleAssistantPage" */ './pages/GoogleAssistantPage'
  )
)
const Home = loadable(() =>
  import(/* webpackChunkName: "Home" */ './pages/Home/Home')
)
const ReferralPage = loadable(() =>
  import(
    /* webpackChunkName: "ReferralPage" */ './pages/ReferralPage/ReferralPage'
  )
)
const KickstarterPage = loadable(() =>
  import(
    /* webpackChunkName: "KickstarterPage" */ './pages/KickstarterPage/KickstarterPage'
  )
)
const OscarHealth = loadable(() =>
  import(
    /* webpackChunkName: "OscarHealth" */ './pages/OscarHealth/OscarHealth'
  )
)

const PlansPage = loadable(() =>
  import(/* webpackChunkName: "PlansPage" */ './pages/PlansPage/PlansPage')
)
const PremiumPage = loadable(() =>
  import(
    /* webpackChunkName: "PremiumPage" */ './pages/PremiumPage/PremiumPage'
  )
)
const RedeemPage = loadable(() =>
  import(/* webpackChunkName: "RedeemPage" */ './pages/Redeem/RedeemPage')
)
const RedeemFormPage = loadable(() =>
  import(
    /* webpackChunkName: "RedeemFormPage" */ './pages/Redeem/RedeemFormPage'
  )
)
const ResetPage = loadable(() =>
  import(/* webpackChunkName: "ResetPage" */ './pages/ResetPage/ResetPage')
)
const SignUpPage = loadable(() =>
  import(/* webpackChunkName: "SignUpPage" */ './pages/SignUpPage')
)
const UpgradePage = loadable(() =>
  import(
    /* webpackChunkName: "UpgradePage" */ './pages/UpgradePage/UpgradePage'
  )
)
const VerifyPage = loadable(() =>
  import(/* webpackChunkName: "VerifyPage" */ './pages/VerifyPage/VerifyPage')
)
const WelcomePage = loadable(() =>
  import(
    /* webpackChunkName: "WelcomePage" */ './pages/WelcomePage/WelcomePage'
  )
)
const WelcomePageOldSignupFlow = loadable(() =>
  import(
    /* webpackChunkName: "WelcomePageOldSignupFlow" */ './pages/WelcomePage/WelcomePageOldSignupFlow'
  )
)
const ZumbaPage = loadable(() =>
  import(/* webpackChunkName: "ZumbaPage" */ './pages/ZumbaPage/ZumbaPage')
)
const Renew = loadable(() =>
  import(/* webpackChunkName: "Renew" */ './pages/Renew/Renew')
)
const NikePage = loadable(() =>
  import(/* webpackChunkName: "NikePage" */ './pages/NikePage/NikePage')
)
const NikePlusFRPage = loadable(() =>
  import(
    /* webpackChunkName: "NikePlusFRPage" */ './pages/NikePage/NikePlusFRPage'
  )
)
const GEPage = loadable(() =>
  import(/* webpackChunkName: "GEPage" */ './pages/GEPage/GEPage')
)

import { useDispatch } from 'react-redux'
import { detectInitialLocale } from './store/localeSlice'
import useLocale from './hooks/useLocale'
const UserAccount = loadable(() =>
  import(
    /* webpackChunkName: "UserAccount" */ './pages/UserAccount/UserAccount'
  )
)
const Deleted = loadable(() =>
  import(
    /* webpackChunkName: "Deleted" */ './pages/UserAccount/screens/Deleted'
  )
)
const HomeChef = loadable(() =>
  import(/* webpackChunkName: "HomeChef" */ './pages/HomeChef/HomeChef')
)
const DietToGo = loadable(() =>
  import(/* webpackChunkName: "DietToGo" */ './pages/DietToGo/DietToGo')
)
const DietToGoBundle = loadable(() =>
  import(
    /* webpackChunkName: "DietToGoBundle" */ './pages/DietToGoBundle/DietToGoBundle'
  )
)
const SimpleFeast = loadable(() =>
  import(
    /* webpackChunkName: "SimpleFeast" */ './pages/SimpleFeast/SimpleFeast'
  )
)
const DenicePage = loadable(() =>
  import(/* webpackChunkName: "DenicePage" */ './pages/DenicePage/DenicePage')
)
const EnelEnergiaPage = loadable(() =>
  import(
    /* webpackChunkName: "EnelEnergiaPage" */ './pages/EnelEnergiaPage/EnelEnergiaPage'
  )
)

const HardRedirect = ({ from, to, ...props }) => {
  // Ensure a redicrect reloads the entire page,
  // useful when redirecting to .html files.
  return (
    <Route
      {...props}
      path={from}
      component={({ location: { search } }) => {
        if (isSSR) return
        const destination = new URL(to, document.location)
        if (search) destination.search = search
        document.location = destination.toString()
      }}
    />
  )
}

HardRedirect.propTypes = {
  from: PropTypes.string,
  to: PropTypes.string,
}

const deeplink = ({ type, fallback }) => {
  if (device.deeplinks[type]) {
    const time = new Date().valueOf()

    setTimeout(() => {
      const delta = new Date().valueOf() - time
      if (delta <= 100) window.location = device.appStoreURL
    }, 25)
    window.location = device.deeplinks[type]
  } else if (fallback) {
    window.location = fallback
  }
}

const deeplinkPremium = () =>
  deeplink({ type: 'premium', fallback: '/premium' })

const translatedRoutes = (base = '') => {
  let key = 1
  return [
    <Route
      exact
      path={`${base}/`}
      component={withPageViewCategory('Offer pages')(Home)}
      key={key++}
    />,
    <Route path={`${base}/signup`} render={() => <SignUpPage />} key={key++} />,
    <Route path={`${base}/premium`} component={PremiumPage} key={key++} />,
    <Route path={`${base}/features`} component={FeaturesPage} key={key++} />,
    <Route
      path={`${base}/welcome`}
      component={WelcomePageOldSignupFlow}
      key={key++}
    />,
    <Route path={`${base}/account`} component={UserAccount} key={key++} />,
    <Route path={`${base}/deleted`} component={Deleted} key={key++} />,
    <Route path={`${base}/reset/*`} component={ResetPage} key={key++} />,
    <Route path={`${base}/redeem/ls5019wb`} component={Renew} key={key++} />,
    <Route
      exact
      path={`${base}/redeem`}
      component={RedeemFormPage}
      key={key++}
    />,
    <Route path={`${base}/redeem/:slug`} component={RedeemPage} key={key++} />,
    <Route
      path={`${base}/3-week-weight-loss`}
      component={KickstarterPage}
      key={key++}
    />,
    <Route path={`${base}/plans`} component={PlansPage} key={key++} />,
    <Route
      path={`${base}/finalize-payment`}
      component={FinalizePaymentPage}
      key={key++}
    />,
    <Route
      path={`${base}/upgrade/welcome`}
      component={WelcomePage}
      key={key++}
    />,
    <Route
      path={`${base}/upgrade/:code?`}
      component={UpgradePage}
      key={key++}
    />,
    <Route
      path={`${base}/renew/welcome`}
      component={WelcomePage}
      key={key++}
    />,
    <Route
      path={`${base}/free/welcome`}
      component={() => <WelcomePage track={false} />}
      key={key++}
    />,
    <Route
      path={`${base}/gift-card/:code?`}
      component={BuyGiftCardPage}
      key={key++}
    />,
    <Route
      path={`${base}/voucher`}
      component={ActivateGiftCardPage}
      key={key++}
    />,
    <Route
      path={`${base}/purchase-complete`}
      component={BuyGiftCardDonePage}
      key={key++}
    />,
    <Route path={`${base}/invite`} component={ReferralPage} key={key++} />,
    <Route path={`${base}/nike`} component={NikePage} key={key++} />,
    <Route
      path={`${base}/nike-50`}
      component={() => <NikePage fiftyPercent />}
      key={key++}
    />,
    <Route path={`${base}/homechef-3m`} component={HomeChef} key={key++} />,
    <Route path={`${base}/ge`} component={GEPage} key={key++} />,
    <Route path={`${base}/simplefeast`} component={SimpleFeast} key={key++} />,
  ]
}

let App = ({
  defaultMessages,
  initialData,
  routes,
  localeOverrides,
  abTests = {},
  cloudfrontViewerCountry,
}) => {
  let { language, currency, country } = useLocale()
  const dispatch = useDispatch()
  useTracking()

  useEffect(() => {
    if (cloudfrontViewerCountry) {
      ga.set('dimension1', cloudfrontViewerCountry)
    }
  }, [cloudfrontViewerCountry])

  useEffect(() => {
    dispatch(detectInitialLocale())
  }, [])

  if ((!language || !currency || !country) && !localeOverrides) {
    return null
  }

  return (
    <SentryBoundary>
      <Title>
        Lifesum Health App - Get Healthy, Lose Weight, or Gain Muscle
      </Title>
      <LanguageProvider defaultMessages={defaultMessages}>
        <AbTestProvider abTests={abTests}>
          <MobileMenuProvider>
            <RouteChangeHandler>
              <PriceUpdater>
                <Switch>
                  <Redirect
                    exact
                    from="/eat-like-denice"
                    to="/redeem/denice30"
                    component={DenicePage}
                  />
                  <Route path="/redeem/denice30" component={DenicePage} />
                  <Route path="/sv/redeem/denice30" component={DenicePage} />
                  <Route
                    path="/redeem/kampanjdec19"
                    component={() => <DenicePage coupon="kampanjdec19" />}
                  />
                  <Route
                    path="/sv/redeem/kampanjdec19"
                    component={() => <DenicePage coupon="kampanjdec19" />}
                  />

                  {/* Pages available in multiple languages */}
                  {allLanguages.map(language =>
                    language === 'en'
                      ? translatedRoutes()
                      : translatedRoutes(`/${language}`)
                  )}

                  {/* Other pages – english only */}
                  <HardRedirect
                    exact
                    from="/mobile-policy"
                    to="/privacy-policy.html"
                  />
                  <HardRedirect
                    exact
                    from="/mobile-policy-de"
                    to="/privacy-policy-de.html"
                  />
                  <Route path="/deeplink/premium" component={deeplinkPremium} />
                  <Route
                    path="/google-assistant"
                    component={GoogleAssistantPage}
                  />

                  {/* Partner pages */}
                  <Route path="/strong-by-zumba" component={ZumbaPage} />
                  <Route path="/strongbyzumba" component={ZumbaPage} />
                  <Route
                    path="/sbz-instructor"
                    component={() => <ZumbaPage instructor={true} />}
                  />
                  <Route path={'/nikeplusfr'} component={NikePlusFRPage} />
                  <Route path={'/ketodiettogo'} component={DietToGo} />
                  <Route path={'/diettogo'} component={DietToGoBundle} />
                  <Route
                    path={'/medstarwhc'}
                    component={() => <DietToGoBundle medstar />}
                  />
                  <Route path={'/oscar'} component={OscarHealth} />
                  <Route path={'/enelenergia'} component={EnelEnergiaPage} />
                  {/* Email landing page */}
                  <Route path="/email/*" component={EmailLandingPage} />
                  <Route path="/friskvard" component={FriskvardPage} />

                  {/* Redirects */}
                  <Redirect exact from="/pischa" to="/redeem/pischa30" />
                  <Redirect exact from="/gold*" to="/premium" />
                  <HardRedirect
                    exact
                    from="/storybook"
                    to="/storybook/index.html"
                  />
                  <Route path="/get-code*" component={RedirectToOldWeb} />
                  <Route path="/verify/:code" component={VerifyPage} />
                  <Route path="/faq" component={RedirectToOtherSites} />
                  <Route path="/help*" component={RedirectToOtherSites} />

                  <Redirect exact from="/basic" to="/redeem/basic" />
                  <Redirect
                    exact
                    from="/stupidgenius"
                    to="/redeem/stupidgenius"
                  />
                  <Redirect exact from="/eatthis" to="/redeem/eatthis" />
                  <Redirect
                    from="/20minfitness"
                    to="/influencer/20minfitness"
                  />

                  <Route
                    path="/influencer/:slug"
                    render={() => <RedeemPage influencer />}
                  />
                  <Redirect exact from="/tyngre" to="/redeem/tyngre50" />
                  <Redirect exact from="/keto-diet" to="/ketogenic-diet" />
                  <Redirect exact from="/get-in-contact" to="/contact" />
                  <Route
                    path="/basic"
                    component={() => (
                      <RedeemPage
                        showAppButtons
                        code="basic"
                        heading="Welcome, Pretty Basic listeners!"
                      />
                    )}
                  />
                  <Route
                    path="/stupidgenius"
                    component={() => (
                      <RedeemPage
                        showAppButtons
                        code="stupidgenius"
                        heading="Welcome, Stupid Genius listeners!"
                      />
                    )}
                  />
                  <Route
                    path="/eatthis"
                    component={() => (
                      <RedeemPage
                        showAppButtons
                        code="eatthis"
                        heading="Welcome, Eat This Not That listeners!"
                      />
                    )}
                  />

                  {routes.map((route, index) => {
                    const languagesToRender = route.translated
                      ? allLanguages
                      : ['en']

                    return languagesToRender.map(language => {
                      const languageSlug =
                        language === 'en' ? '' : `/${language}`
                      return (
                        <Route
                          key={`${language}-${index}`}
                          path={`${languageSlug}${route.path}`}
                          exact
                          render={props =>
                            React.createElement(route.component, {
                              ...props,
                              initialData,
                              getInitialProps: route.getInitialProps,
                              ...route.props,
                            })
                          }
                        />
                      )
                    })
                  })}
                </Switch>
              </PriceUpdater>
            </RouteChangeHandler>
          </MobileMenuProvider>
        </AbTestProvider>
      </LanguageProvider>
    </SentryBoundary>
  )
}

App.propTypes = {
  abTests: PropTypes.object,
  cloudfrontViewerCountry: PropTypes.string,
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      component: PropTypes.oneOfType([
        PropTypes.node,
        // loadable promise
        PropTypes.shape({
          then: PropTypes.func,
        }),
        PropTypes.func,
      ]).isRequired,
      exact: PropTypes.bool,
      translated: PropTypes.bool,
    })
  ),
  initialData: PropTypes.array,
  defaultMessages: PropTypes.object,
  localeOverrides: PropTypes.shape({
    language: PropTypes.string,
    currency: PropTypes.string,
    country: PropTypes.string,
  }),
}

if (process.env.HOT_RELOADING === 'true') {
  App = hot(App)
}

export default App
